import { formatINR } from "@/utils/formatINR";
import DecimalInput from "@/components/DecimalInput";

const MODES = [
  { value: "per_piece", label: "₹/pc",     hint: "Direct entry" },
  { value: "per_kg",    label: "₹/kg",     hint: "× Net Weight" },
  { value: "per_sqin",  label: "₹/sq.in",  hint: "× Surface Area" },
];

export default function TreatmentField({ label, value, onChange, netWeightKg, placeholderNotes, disabled, testId }) {
  const v = value || { mode: "per_piece", rate: 0, surface_area_sqin: null, notes: "" };

  const computed = (() => {
    const rate = Number(v.rate) || 0;
    if (v.mode === "per_piece") return rate;
    if (v.mode === "per_kg") return rate * (Number(netWeightKg) || 0);
    if (v.mode === "per_sqin") return rate * (Number(v.surface_area_sqin) || 0);
    return 0;
  })();

  const formula = (() => {
    const rate = Number(v.rate) || 0;
    if (v.mode === "per_kg") return `${rate.toFixed(2)} × ${(Number(netWeightKg) || 0).toFixed(3)} kg`;
    if (v.mode === "per_sqin") return `${rate.toFixed(2)} × ${(Number(v.surface_area_sqin) || 0).toFixed(2)} sq.in`;
    return "Direct ₹/pc entry";
  })();

  const setMode = (mode) => onChange({ ...v, mode });

  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white" data-testid={testId}>
      <div className="flex justify-between items-start mb-2 gap-3">
        <label className="text-sm font-semibold text-slate-800">{label}</label>
        <div className="text-right flex-shrink-0">
          <div className="text-2xl font-bold text-orange-600 leading-tight font-mono">{formatINR(computed)}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">/pc</div>
        </div>
      </div>

      <div className="flex gap-1 mb-3">
        {MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            disabled={disabled}
            onClick={() => setMode(m.value)}
            className={`flex-1 px-2 py-1 text-xs rounded border transition ${
              v.mode === m.value
                ? "bg-orange-600 text-white border-orange-600 font-semibold"
                : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[11px] text-slate-500 mb-0.5">Rate ({MODES.find((m) => m.value === v.mode)?.label})</label>
          <DecimalInput
            disabled={disabled}
            value={v.rate ?? 0}
            onChange={(num) => onChange({ ...v, rate: num })}
            className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right"
          />
        </div>
        {v.mode === "per_sqin" && (
          <div>
            <label className="block text-[11px] text-slate-500 mb-0.5">Surface Area (sq.in)</label>
            <DecimalInput
              disabled={disabled}
              value={v.surface_area_sqin ?? 0}
              onChange={(num) => onChange({ ...v, surface_area_sqin: num })}
              className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right"
            />
          </div>
        )}
        <div className={v.mode === "per_sqin" ? "col-span-2" : ""}>
          <label className="block text-[11px] text-slate-500 mb-0.5">Notes</label>
          <input
            type="text" disabled={disabled}
            value={v.notes ?? ""}
            onChange={(e) => onChange({ ...v, notes: e.target.value })}
            placeholder={placeholderNotes || ""}
            className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
          />
        </div>
      </div>

      <div className="mt-2 text-[11px] text-slate-500 font-mono">
        {formula} = {formatINR(computed)}
      </div>
    </div>
  );
}
